import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import { authFlowHeaderIcons } from "./authFlowHeaderIcons";
import { AuthSuccessContent } from "./loginSuccessUi";
import {
  getLoginDividerFooterSx,
  getLoginFieldSx,
  getLoginFooterLinkBoxSx,
  getLoginPrimaryButtonSx,
  LoginCardContent,
  LoginFormHeader,
} from "./loginUi";

export type TwoFactorFormProps = {
  mode: "app" | "sms";
  onSubmit?: (token: string) => void;
  onResend?: () => void;
  error?: string;
  loading?: boolean;
  success?: boolean;
  resendSuccess?: boolean;
  switchUrl?: string;
  translations: {
    verifyIdentity: string;
    appTokenInstructions: string;
    smsTokenInstructions: string;
    token: string;
    tokenPlaceholder: string;
    continue: string;
    switchToSms: string;
    smsToken: string;
    switchToApp: string;
    appToken: string;
    resendToken: string;
    tokenResent: string;
    fieldRequired: string;
  };
};

export const TwoFactorForm = ({
  mode,
  onSubmit,
  onResend,
  error,
  loading = false,
  success = false,
  resendSuccess = false,
  switchUrl,
  translations: t,
}: TwoFactorFormProps) => {
  const theme = useTheme();
  const [token, setToken] = useState("");
  const [tokenError, setTokenError] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTokenError("");

    if (!token.trim()) {
      setTokenError(t.fieldRequired);
      return;
    }

    onSubmit?.(token);
  };

  const defaultSwitchUrl =
    mode === "app"
      ? "/accounts/verify_sms_token/"
      : "/accounts/verify_app_token/";

  const fieldSx = getLoginFieldSx(theme, 3);

  return (
    <LoginCardContent
      component="form"
      onSubmit={handleSubmit}
      testId="two-factor-form"
    >
      <AuthSuccessContent
        success={success}
        contentTestId="two-factor-form-success-content"
        indicatorTestId="two-factor-success-indicator"
      >
        <LoginFormHeader
          title={t.verifyIdentity}
          subtitle={
            mode === "app" ? t.appTokenInstructions : t.smsTokenInstructions
          }
          headerIcon={
            mode === "app"
              ? authFlowHeaderIcons.twoFactorApp
              : authFlowHeaderIcons.twoFactorSms
          }
          headerTestId="two-factor-form-header"
          titleTestId="two-factor-form-title"
          headerIconTestId="two-factor-form-header-icon"
        />

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2.5 }}
            data-testid="two-factor-error"
          >
            {error}
          </Alert>
        ) : null}

        {resendSuccess ? (
          <Alert
            severity="success"
            sx={{ mb: 2.5 }}
            data-testid="two-factor-resend-success"
          >
            {t.tokenResent}
          </Alert>
        ) : null}

        <TextField
          fullWidth
          label={t.token}
          value={token}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "").slice(0, 6);
            setToken(val);
          }}
          error={!!tokenError}
          helperText={tokenError}
          placeholder={t.tokenPlaceholder}
          autoFocus
          autoComplete="one-time-code"
          sx={fieldSx}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
                "data-testid": "two-factor-token",
                style: {
                  letterSpacing: "0.5em",
                  fontSize: "1.5rem",
                  textAlign: "center",
                  fontWeight: 600,
                },
              },
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || success}
          data-testid="two-factor-submit"
          sx={getLoginPrimaryButtonSx(theme)}
        >
          {success ? (
            <CheckIcon fontSize="small" />
          ) : loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t.continue
          )}
        </Button>

        {mode === "sms" && onResend ? (
          <Box sx={getLoginFooterLinkBoxSx()}>
            <Link
              component="button"
              type="button"
              onClick={onResend}
              underline="hover"
              color="text.secondary"
              sx={{ fontSize: "0.875rem", fontWeight: 500 }}
              data-testid="resend-token-link"
            >
              {t.resendToken}
            </Link>
          </Box>
        ) : null}

        <Box sx={getLoginDividerFooterSx()}>
          <Typography variant="body2" color="text.secondary">
            {mode === "app" ? t.switchToSms : t.switchToApp}{" "}
            <Link
              href={switchUrl ?? defaultSwitchUrl}
              underline="hover"
              data-testid="switch-method-link"
            >
              {mode === "app" ? t.smsToken : t.appToken}
            </Link>
          </Typography>
        </Box>
      </AuthSuccessContent>
    </LoginCardContent>
  );
};
